// src/features/imports/state/imports.reducer.ts

import { Driver } from '@/lib/api/company/drivers/types'
import type {
  ImportsFeatureState,
  ImportsReadModel,
  DriverImportId,
  VehicleImportId,
  SimpleDriverModel,
  SimpleVehicleModel,
} from '../model/imports.state.types'

import { createInitialFeatureState } from './imports.initialState'
import { Vehicle } from '@/lib/api/company/vehicle-list/types'

// -------------------- Actions --------------------

export type ImportsAction =
  | { type: 'INIT_FROM_READ_MODEL'; payload: { readModel: ImportsReadModel } }

  // selection - drivers
  | { type: 'TOGGLE_DRIVER_SELECTED'; payload: { importId: DriverImportId } }
  | { type: 'SELECT_ALL_DRIVERS'; payload: { importIds: DriverImportId[] } }
  | { type: 'CLEAR_SELECTED_DRIVERS' }

  // selection - vehicles
  | { type: 'TOGGLE_VEHICLE_SELECTED'; payload: { importId: VehicleImportId } }
  | { type: 'SELECT_ALL_VEHICLES'; payload: { importIds: VehicleImportId[] } }
  | { type: 'CLEAR_SELECTED_VEHICLES' }

  // edits
  | {
      type: 'SET_DRIVER_DATE_FROM'
      payload: { importId: DriverImportId; dateFrom: string | null }
    }
  | {
      type: 'SET_DRIVER_ASSIGNED_TO'
      payload: { importId: DriverImportId; assignedTo?: Driver }
    }
  | {
      type: 'SET_VEHICLE_ASSIGNED_TO'
      payload: { importId: VehicleImportId; assignedTo?: Vehicle }
    }

  // removed
  | { type: 'TOGGLE_DRIVER_REMOVED'; payload: { importId: DriverImportId } }
  | { type: 'TOGGLE_VEHICLE_REMOVED'; payload: { importId: VehicleImportId } }
  | { type: 'CLEAR_REMOVED' }

// -------------------- helpers --------------------

function toggleInSet<T>(set: Set<T>, value: T): Set<T> {
  const next = new Set(set)
  if (next.has(value)) next.delete(value)
  else next.add(value)
  return next
}

function addManyToSet<T>(set: Set<T>, values: T[]): Set<T> {
  const next = new Set(set)
  for (const v of values) next.add(v)
  return next
}

function removeFromSet<T>(set: Set<T>, value: T): Set<T> {
  if (!set.has(value)) return set
  const next = new Set(set)
  next.delete(value)
  return next
}

// -------------------- Reducer --------------------

export function importsReducer(
  state: ImportsFeatureState,
  action: ImportsAction,
): ImportsFeatureState {
  switch (action.type) {
    case 'INIT_FROM_READ_MODEL': {
      const initial = createInitialFeatureState(action.payload.readModel)

      return initial
    }

    // ------- selection: drivers -------

    case 'TOGGLE_DRIVER_SELECTED': {
      const { importId } = action.payload
      if (state.ui.removedDriverImportIds.has(importId)) return state

      return {
        ...state,
        ui: {
          ...state.ui,
          selectedDriverImportIds: toggleInSet(
            state.ui.selectedDriverImportIds,
            importId,
          ),
        },
      }
    }

    case 'SELECT_ALL_DRIVERS': {
      const ids = action.payload.importIds.filter(
        (id) => !state.ui.removedDriverImportIds.has(id),
      )
      return {
        ...state,
        ui: {
          ...state.ui,
          selectedDriverImportIds: addManyToSet(
            state.ui.selectedDriverImportIds,
            ids,
          ),
        },
      }
    }

    case 'CLEAR_SELECTED_DRIVERS':
      const nextSelected = new Set(
        Array.from(state.ui.selectedDriverImportIds).filter((importId) => {
          const driver = state.readModel.driverItemsById[importId]
          return driver?.bucket === 'existing'
        }),
      )
      return {
        ...state,
        ui: {
          ...state.ui,
          selectedDriverImportIds: nextSelected,
        },
      }

    // ------- selection: vehicles -------

    case 'TOGGLE_VEHICLE_SELECTED': {
      const { importId } = action.payload
      if (state.ui.removedVehicleImportIds.has(importId)) return state

      return {
        ...state,
        ui: {
          ...state.ui,
          selectedVehicleImportIds: toggleInSet(
            state.ui.selectedVehicleImportIds,
            importId,
          ),
        },
      }
    }

    case 'SELECT_ALL_VEHICLES': {
      const ids = action.payload.importIds.filter(
        (id) => !state.ui.removedVehicleImportIds.has(id),
      )
      return {
        ...state,
        ui: {
          ...state.ui,
          selectedVehicleImportIds: addManyToSet(
            state.ui.selectedVehicleImportIds,
            ids,
          ),
        },
      }
    }

    case 'CLEAR_SELECTED_VEHICLES':
      const nextSelectedVehicles = new Set(
        Array.from(state.ui.selectedVehicleImportIds).filter((importId) => {
          const driver = state.readModel.vehicleItemsById[importId]
          return driver?.bucket === 'existing'
        }),
      )
      return {
        ...state,
        ui: {
          ...state.ui,
          selectedVehicleImportIds: nextSelectedVehicles,
        },
      }
    // ------- edits -------

    case 'SET_DRIVER_DATE_FROM': {
      const { importId, dateFrom } = action.payload
      return {
        ...state,
        ui: {
          ...state.ui,
          driverDateFromByImportId: {
            ...state.ui.driverDateFromByImportId,
            [importId]: dateFrom,
          },
        },
      }
    }

    case 'SET_DRIVER_ASSIGNED_TO': {
      const { importId, assignedTo } = action.payload
      return {
        ...state,
        ui: {
          ...state.ui,
          driverAssignedToByImportId: {
            ...state.ui.driverAssignedToByImportId,
            [importId]: assignedTo,
          },
        },
      }
    }

    case 'SET_VEHICLE_ASSIGNED_TO': {
      const { importId, assignedTo } = action.payload
      return {
        ...state,
        ui: {
          ...state.ui,
          vehicleAssignedToByImportId: {
            ...state.ui.vehicleAssignedToByImportId,
            [importId]: assignedTo,
          },
        },
      }
    }

    // ------- removed -------
    // UX: removing an item auto-unselects it.

    case 'TOGGLE_DRIVER_REMOVED': {
      const { importId } = action.payload
      const removedNext = toggleInSet(state.ui.removedDriverImportIds, importId)
      const selectedNext = removedNext.has(importId)
        ? removeFromSet(state.ui.selectedDriverImportIds, importId)
        : state.ui.selectedDriverImportIds

      return {
        ...state,
        ui: {
          ...state.ui,
          removedDriverImportIds: removedNext,
          selectedDriverImportIds: selectedNext,
        },
      }
    }

    case 'TOGGLE_VEHICLE_REMOVED': {
      const { importId } = action.payload
      const removedNext = toggleInSet(
        state.ui.removedVehicleImportIds,
        importId,
      )
      const selectedNext = removedNext.has(importId)
        ? removeFromSet(state.ui.selectedVehicleImportIds, importId)
        : state.ui.selectedVehicleImportIds

      return {
        ...state,
        ui: {
          ...state.ui,
          removedVehicleImportIds: removedNext,
          selectedVehicleImportIds: selectedNext,
        },
      }
    }

    case 'CLEAR_REMOVED':
      return {
        ...state,
        ui: {
          ...state.ui,
          removedDriverImportIds: new Set(),
          removedVehicleImportIds: new Set(),
        },
      }

    default:
      return state
  }
}
